import {TextAreaPrompt} from "./TextAreaPrompt.jsx"
import {OneToTenPrompt} from "./OneToTenPrompt.jsx"
// import { BooleanPrompt } from "./BooleanPrompt.jsx"
import {VerticalRadioPrompt} from "./VerticalRadioPrompt.jsx"
import {VerticalCheckboxPrompt} from "./VerticalCheckboxPrompt.jsx"
import {RadioPrompt} from "./shared/RadioPrompt.jsx"
import {SelectFromApiPrompt} from "./SelectFromApiPrompt.jsx"
import SelectPreceptor from "./SelectPreceptor.jsx"
import TopicIntro from "../surveys/osr/TopicIntro.jsx"
import CaseyFink from "./CaseyFink.jsx"

import {QUESTION_TYPE} from "../../utilities/general.jsx"
import CaseyFinkNoNa from "./CaseyFinkNoNa.jsx"

function QuestionRenderer({
    question,
    questionTypeNameById,
    value,
    onChange,
    otherValue,
    onOtherChange,
    emptyError,
    questionCategories,
    surveyType
}) {
    const typeName = questionTypeNameById[question.question_type_id]
    const field = `question_${question.question_id}`

    switch (typeName) {
        case QUESTION_TYPE.RANGE:
            const options = question.answers
                .slice()
                .sort((a, b) => Number(a.description) - Number(b.description))
                .map((a) => ({label: a.description, value: String(a.id)}))
            const maxLabel = options.length > 0 ? options[options.length - 1].label : ""
            let scaleStart = "1: Not Ready at All"
            let scaleEnd = `Ready Today: ${maxLabel}`

            if (surveyType === "osr") {
                scaleStart = "1. Requires Preceptor"
                scaleEnd = `Independent: ${maxLabel}`
            }

            return (
                <OneToTenPrompt
                    questionText={question.question_description}
                    field={field}
                    options={options}
                    scaleStart={scaleStart}
                    scaleEnd={scaleEnd}
                    data={value}
                    handle={(val) => onChange(val)}
                    isRequired={question?.is_required ?? true}
                />
            )

        case QUESTION_TYPE.NUMBER:
        case QUESTION_TYPE.DATE:
        case QUESTION_TYPE.TEXT:
            // number/date use TextAreaPrompt as a placeholder until dedicated components exist
            return (
                <TextAreaPrompt
                    questionText={question.question_description}
                    field={field}
                    data={value ?? ""}
                    handle={(val) => onChange(val)}
                    isRequired={question?.is_required ?? true}
                />
            )

        case QUESTION_TYPE.SELECT: {
            const options = question.answers.map((a) => ({
                label: a.description,
                value: String(a.id),
            }))
            return (
                <VerticalRadioPrompt
                    questionText={question.question_description}
                    field={field}
                    options={options}
                    data={value}
                    handle={(val) => onChange(val)}
                    isRequired={question?.is_required ?? true}
                />
            )
        }

        case QUESTION_TYPE.BOOLEAN: {
            const options = question.answers.map((a) => ({
                label: a.description,
                value: String(a.id),
            }))
            return (
                <VerticalRadioPrompt
                    questionText={question.question_description}
                    field={field}
                    options={options}
                    data={value}
                    handle={(val) => onChange(val)}
                    isRequired={question?.is_required ?? true}
                />
            )
        }

        case QUESTION_TYPE.MULTI_SELECT_WITH_TEXT: {
            const options = question.answers.map((a) => String(a.id))
            const optionLabelByValue = Object.fromEntries(question.answers.map((a) => [String(a.id), a.description]))
            const otherAnswer = question.answers.find((a) => a.description === "Other")
            const otherOptionValue = otherAnswer ? String(otherAnswer.id) : undefined

            return (
                <VerticalCheckboxPrompt
                    questionText={question.question_description}
                    field={field}
                    options={options}
                    optionLabelByValue={optionLabelByValue}
                    data={value ?? []}
                    handleMultiple={(val) => {
                        const current = value ?? []
                        const next = current.includes(val) ? current.filter((v) => v !== val) : [...current, val]
                        onChange(next)
                    }}
                    otherOptionValue={otherOptionValue}
                    otherText={otherValue}
                    onOtherTextChange={onOtherChange}
                    isRequired={question?.is_required ?? true}
                />
            )
        }

        case QUESTION_TYPE.RANGE_WITH_NOT_APPLICABLE: {
            const naAnswer = question.answers.find((a) => a.description === "0")
            const options = question.answers
                .filter((a) => a.description !== "0")
                .sort((a, b) => Number(a.description) - Number(b.description))
                .map((a) => ({label: a.description, value: String(a.id)}))
            const maxLabel = options.length > 0 ? options[options.length - 1].label : ""

            let scaleStart = "1: Not Ready at All"
            let scaleEnd = `Ready Today: ${maxLabel}`

            if (surveyType === "osr") {
                scaleStart = "1. Requires Preceptor"
                scaleEnd = `Independent: ${maxLabel}`
            }

            return (
                <RadioPrompt
                    questionText={question.question_description}
                    field={field}
                    options={options}
                    scaleStart={scaleStart}
                    scaleEnd={scaleEnd}
                    includeNA
                    naValue={naAnswer ? String(naAnswer.id) : undefined}
                    data={value}
                    handle={(val) => onChange(val)}
                    isRequired={question?.is_required ?? true}
                />
            )
        }
        case QUESTION_TYPE.SELECT_FROM_API:
            return (
                <SelectFromApiPrompt
                    orientation_id={question.orientation_id}
                    questionText={question.question_description}
                    field={field}
                    apiPath={question.question_api_path}
                    data={value}
                    handle={(val) => onChange(val)}
                    isRequired={question?.is_required ?? true}
                />
            )

        case QUESTION_TYPE.TOPIC_INTRO:
            const category = questionCategories.find((c) => c.id === question.question_category_id)

            let showRatingScale = true
            if (category.name === "Additional Input") {
                // this topis is for text entry and does not require the rating scale
                showRatingScale = false
            }

            return (
                <TopicIntro
                    topicRange={question.question_description}
                    sectionName={category.name}
                    sectionDescription={category.description}
                    isTopicPrompt={true}
                    showRatingScale={showRatingScale}
                    isRequired={question?.is_required ?? true}
                />
            )

        case QUESTION_TYPE.SELECT_PRECEPTOR:
            return (
                <SelectPreceptor
                    orientation_id={question.orientation_id}
                    questionText={question.question_description}
                    field={field}
                    data={value}
                    handle={(val) => onChange(val)}
                    isRequired={question?.is_required ?? true}
                />
            )

        case QUESTION_TYPE.CASEYFINK:
            const caseyOptions = question.answers
                .slice()
                .map((a) => ({label: a.description, value: String(a.id)}))

            return (
                <CaseyFink
                    questionText={question.question_description}
                    field={field}
                    options={caseyOptions}
                    data={value}
                    handle={(val) => onChange(val)}
                    isRequired={question?.is_required ?? true}
                />
            )

        case QUESTION_TYPE.CASEYFINKNONA:
            // do not show zero
            const caseyNoNaOptions = question.answers
                .filter((a) => a.description !== 0 && a.description !== "0")
                .map((a) => ({
                    label: a.description,
                    value: String(a.id),
                }));

            return (
                <CaseyFinkNoNa
                    questionText={question.question_description}
                    field={field}
                    options={caseyNoNaOptions}
                    data={value}
                    handle={(val) => onChange(val)}
                    isRequired={question?.is_required ?? true}
                />
            )

        default:
            console.warn(`No component mapped for question type "${typeName}"`, question)
            return null
    }
}

export default QuestionRenderer
